import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Utensils, Clock, ChevronRight, Salad, Coffee, Moon, Apple,
  Info, Calendar, CheckCircle, Circle, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MEAL_ICONS = {
  breakfast: Coffee,
  lunch: Utensils,
  dinner: Moon,
  snack: Apple
};

const MEAL_COLORS = {
  breakfast: 'bg-amber-100 text-amber-700',
  lunch: 'bg-green-100 text-green-700',
  dinner: 'bg-purple-100 text-purple-700',
  snack: 'bg-blue-100 text-blue-700'
};

export default function MyMealPlan() {
  const navigate = useNavigate();
  const [mealPlan, setMealPlan] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingMeal, setLoggingMeal] = useState(null);
  const [expandedMeal, setExpandedMeal] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch meal plan and compliance in parallel
      const [mealPlanRes, complianceRes] = await Promise.all([
        fetch(`${API_URL}/api/my-meal-plan/today`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/compliance/my-today`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (mealPlanRes.ok) {
        const data = await mealPlanRes.json();
        setMealPlan(data);
      }
      
      if (complianceRes.ok) {
        const data = await complianceRes.json();
        setCompliance(data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLog = async (mealType) => {
    setLoggingMeal(mealType);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/log-meal/quick/${mealType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${mealType.charAt(0).toUpperCase() + mealType.slice(1)} logged!`);
        // Refresh compliance data
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to log meal');
      }
    } catch (error) {
      toast.error('Failed to log meal');
    } finally {
      setLoggingMeal(null);
    }
  };

  const isMealLogged = (mealType) => {
    return compliance?.logged_meals?.includes(mealType.toLowerCase());
  };

  if (loading) {
    return (
      <Card className="animate-pulse">
        <CardContent className="py-8">
          <div className="h-32 bg-gray-100 rounded"></div>
        </CardContent>
      </Card>
    );
  }

  if (!mealPlan?.has_meal_plan) {
    return null; // Don't show if no meal plan
  }

  const { template_name, calorie_level, today_meals, dietitian_name, dietitian_notes } = mealPlan;
  const compliancePercentage = compliance?.compliance_percentage || 0;

  return (
    <Card data-testid="meal-plan-card" className="border-0 shadow-md overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-green-500 to-emerald-400 text-white pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Salad className="w-5 h-5" />
            Today's Meal Plan
          </CardTitle>
          <Badge variant="secondary" className="bg-white/20 text-white">
            {calorie_level} kcal
          </Badge>
        </div>
        <p className="text-sm text-white/80 mt-1">{template_name}</p>
        
        {/* Compliance Progress */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-white/80">Today's Progress</span>
            <span className="font-medium">{Math.round(compliancePercentage)}%</span>
          </div>
          <div className="w-full bg-white/30 rounded-full h-2">
            <div 
              className="bg-white rounded-full h-2 transition-all duration-500"
              style={{ width: `${Math.min(100, compliancePercentage)}%` }}
            />
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-4 space-y-3">
        {/* Dietitian Notes */}
        {dietitian_notes && (
          <div className="p-3 bg-orange-50 rounded-lg border-l-4 border-orange-400">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-700">{dietitian_notes}</p>
                <p className="text-xs text-gray-500 mt-1">— {dietitian_name}</p>
              </div>
            </div>
          </div>
        )}

        {/* Meals */}
        {today_meals?.map((meal, index) => {
          const Icon = MEAL_ICONS[meal.meal_type] || Utensils;
          const colorClass = MEAL_COLORS[meal.meal_type] || 'bg-gray-100 text-gray-700';
          const isExpanded = expandedMeal === index;
          const logged = isMealLogged(meal.meal_type);
          const isLogging = loggingMeal === meal.meal_type;

          return (
            <div 
              key={index}
              className={`border rounded-lg overflow-hidden ${logged ? 'border-green-200 bg-green-50/30' : ''}`}
              data-testid={`meal-${meal.meal_type}`}
            >
              <div className="flex items-center justify-between p-3">
                <button
                  className="flex items-center gap-3 flex-1 text-left hover:bg-gray-50 -m-3 p-3 transition-colors"
                  onClick={() => setExpandedMeal(isExpanded ? null : index)}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-gray-900 capitalize">{meal.meal_type}</p>
                      {logged && (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {meal.time_range}
                    </p>
                  </div>
                  <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </button>
                
                {/* Quick Log Button */}
                {!logged && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-2 text-green-600 border-green-200 hover:bg-green-50"
                    onClick={() => handleQuickLog(meal.meal_type)}
                    disabled={isLogging}
                    data-testid={`log-${meal.meal_type}-btn`}
                  >
                    {isLogging ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Circle className="w-3 h-3 mr-1" />
                        Log
                      </>
                    )}
                  </Button>
                )}
              </div>

              {isExpanded && (
                <div className="px-3 pb-3 border-t bg-gray-50">
                  <ul className="space-y-2 mt-3">
                    {meal.items.map((item, itemIndex) => (
                      <li 
                        key={itemIndex}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className={`${item.is_optional ? 'text-gray-500 italic' : 'text-gray-700'}`}>
                          {item.food_name}
                          {item.is_optional && <span className="text-xs ml-1">(optional)</span>}
                        </span>
                        <span className="text-gray-500 text-xs bg-white px-2 py-1 rounded">
                          {item.portion}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        })}

        {/* View Full Plan Link */}
        <Button 
          variant="ghost" 
          className="w-full justify-between text-green-600 hover:text-green-700 hover:bg-green-50 mt-2"
          onClick={() => navigate('/meal-plan')}
          data-testid="view-full-meal-plan-btn"
        >
          View Full Week Plan
          <Calendar className="w-4 h-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
